import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { chatApi } from '../api/chat-api';
import type { SendMessageRequest } from './types';

export const chatKeys = {
  all: ['chat'] as const,
  conversations: () => [...chatKeys.all, 'conversations'] as const,
  conversation: (id: string) => [...chatKeys.all, 'conversation', id] as const,
  messages: (id: string) => [...chatKeys.all, 'messages', id] as const,
  unread: () => [...chatKeys.all, 'unread'] as const,
};

export function useConversationsQuery() {
  return useQuery({
    queryKey: chatKeys.conversations(),
    queryFn: () => chatApi.listConversations(0, 50),
    staleTime: 15_000,
  });
}

export function useConversationQuery(id: string | undefined) {
  return useQuery({
    queryKey: chatKeys.conversation(id ?? ''),
    queryFn: () => chatApi.getConversation(id!),
    enabled: Boolean(id),
  });
}

export function useMessagesInfiniteQuery(conversationId: string | undefined) {
  return useInfiniteQuery({
    queryKey: chatKeys.messages(conversationId ?? ''),
    queryFn: ({ pageParam }) => chatApi.listMessages(conversationId!, pageParam as number, 50),
    enabled: Boolean(conversationId),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.page + 1 < last.totalPages ? last.page + 1 : undefined),
    staleTime: 5_000,
  });
}

export function useSendMessageMutation(conversationId: string) {
  return useMutation({
    mutationFn: (body: SendMessageRequest) => chatApi.sendMessage(conversationId, body),
  });
}

export function useMarkReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => chatApi.markRead(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chatKeys.conversations() });
      void qc.invalidateQueries({ queryKey: chatKeys.unread() });
    },
  });
}

export function useUnreadCountQuery() {
  return useQuery({
    queryKey: chatKeys.unread(),
    queryFn: () => chatApi.unreadCount(),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}
